/**
 * AudioTimelineChart - Interactive timeline visualization for audio analysis
 * 
 * Follows Spotify's Audio Analysis API hierarchy:
 * https://developer.spotify.com/documentation/web-api/reference/get-audio-analysis
 * 
 * Visual Hierarchy (top to bottom):
 * 1. Sections - Largest structures (verse, chorus, bridge, etc.) defined by large variations in rhythm/timbre
 * 2. Bars - Musical measures, typically containing 4 beats in 4/4 time
 * 3. Beats - Basic time unit of music (the main pulse you tap to)
 * 4. Tatums - Subdivisions of beats (lowest regular pulse train, e.g., 8th notes)
 * 5. Segments - Most granular audio features (timbre, pitch, loudness changes)
 * 
 * Relationships:
 * - Tatums are subdivisions of beats (2-4 tatums per beat)
 * - Beats are the main pulse (4 beats per bar in 4/4 time)
 * - Bars group beats into measures
 * - Sections group bars into song structures
 * - Segments analyze timbral content independent of rhythm
 */

import { Box, Typography, Paper, IconButton, ButtonGroup, Slider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
// ⚡ Path-based imports (6× faster than barrel imports)
import ZoomIn from '@mui/icons-material/ZoomIn';
import ZoomOut from '@mui/icons-material/ZoomOut';
import ZoomOutMap from '@mui/icons-material/ZoomOutMap';
import { useRef, useEffect, useState, useCallback } from 'react';
import { analytics } from '@/lib/analytics';
import { tempoAtTime } from '@/lib/audioAnalysis';
import type { TempoTick } from '@/lib/audio-analysis/schema.gen';

interface TimelineSection {
  start: number;
  duration: number;
  confidence?: number;
}

interface AudioTimelineChartProps {
  data: {
    track?: {
      duration?: number;
    };
    beats?: TimelineSection[];
    bars?: TimelineSection[];
    tatums?: TimelineSection[];
    sections?: TimelineSection[];
    segments?: TimelineSection[];
    tempo_ticks?: TempoTick[];
  };
  currentPosition?: number; // Current playback position in seconds
  onSeek?: (positionSeconds: number) => void; // Callback when user clicks to seek
}

// Original vibrant colors from the legacy UI
const COLORS = [
  'rgba(30,215,96, 0.9)',
  'rgba(245,115,160, 0.9)',
  'rgba(80,155,245, 0.9)',
  'rgba(255,100,55, 0.9)',
  'rgba(180,155,200, 0.9)',
  'rgba(250,230,45, 0.9)',
  'rgba(0,100,80, 0.9)',
  'rgba(175,40,150, 0.9)',
  'rgba(30,50,100, 0.9)',
];

export function AudioTimelineChart({ data, currentPosition = 0, onSeek }: AudioTimelineChartProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [hoveredInfo, setHoveredInfo] = useState<string | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number } | null>(null);
  
  // Zoom and pan state
  const [zoomLevel, setZoomLevel] = useState(1); // 1 = full view, 2 = 2x zoom, etc.
  const [viewStart, setViewStart] = useState(0); // Start time of viewport in seconds
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartViewStart, setDragStartViewStart] = useState(0);

  // Calculate viewport duration based on zoom
  const duration = data.track?.duration || 0;
  const viewDuration = duration / zoomLevel;
  const viewEnd = Math.min(viewStart + viewDuration, duration);
  
  // Constrain viewStart
  useEffect(() => {
    const maxStart = Math.max(0, duration - viewDuration);
    if (viewStart > maxStart) {
      setViewStart(maxStart);
    }
  }, [viewStart, duration, viewDuration]);
  
  // Auto-follow playback when zoomed (keep position centered)
  useEffect(() => {
    if (zoomLevel > 1 && currentPosition > 0 && !isDragging) {
      // Only auto-follow if position is near the edges of viewport
      const margin = viewDuration * 0.2; // 20% margin
      if (currentPosition < viewStart + margin || currentPosition > viewEnd - margin) {
        // Center the position in viewport
        const newViewStart = Math.max(0, Math.min(
          duration - viewDuration,
          currentPosition - viewDuration / 2
        ));
        setViewStart(newViewStart);
      }
    }
  }, [currentPosition, zoomLevel, viewStart, viewEnd, viewDuration, duration, isDragging]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data.track?.duration) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size (2x for retina)
    const container = canvas.parentElement;
    if (!container) return;

    canvas.style.width = `${container.offsetWidth}px`;
    canvas.style.height = '500px';
    canvas.width = container.offsetWidth * 2;
    canvas.height = 500 * 2;

    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Collect all array-like data in correct hierarchy (top to bottom)
    // Sections (largest) → Bars → Beats → Tatums (smallest) → Segments (audio features)
    const arrayLikes: { name: string; data: TimelineSection[]; color: string }[] = [];
    
    if (data.sections && data.sections.length > 0) arrayLikes.push({ name: 'Sections', data: data.sections, color: '#4CAF50' });
    if (data.bars && data.bars.length > 0) arrayLikes.push({ name: 'Bars', data: data.bars, color: '#2196F3' });
    if (data.beats && data.beats.length > 0) arrayLikes.push({ name: 'Beats', data: data.beats, color: '#FF9800' });
    if (data.tatums && data.tatums.length > 0) arrayLikes.push({ name: 'Tatums', data: data.tatums, color: '#9C27B0' });
    if (data.segments && data.segments.length > 0) arrayLikes.push({ name: 'Segments', data: data.segments, color: '#607D8B' });

    const dtempoTicks = (data.tempo_ticks ?? []).filter((tick) => tick.source === 'dtempo');
    const extraRows = dtempoTicks.length > 0 ? 1 : 0;
    if (arrayLikes.length === 0 && extraRows === 0) return;

    const rowHeight = height / (arrayLikes.length + extraRows);

    // Draw each timeline row
    arrayLikes.forEach((arrayLike, arrayLikeIndex) => {
      const startY = arrayLikeIndex * rowHeight;

      // Filter items visible in current viewport
      const visibleItems = arrayLike.data.filter(item => 
        item.start < viewEnd && (item.start + item.duration) > viewStart
      );

      // Draw each section/beat/etc
      visibleItems.forEach((item) => {
        // Calculate position relative to viewport
        const itemStart = Math.max(item.start, viewStart);
        const itemEnd = Math.min(item.start + item.duration, viewEnd);
        const itemDuration = itemEnd - itemStart;
        
        const x = ((itemStart - viewStart) / viewDuration) * width;
        const w = (itemDuration / viewDuration) * width;
        
        // Use alternating colors for visual distinction
        const colorIndex = arrayLike.data.indexOf(item);
        ctx.fillStyle = COLORS[colorIndex % COLORS.length];
        ctx.fillRect(x, startY, w, rowHeight);
        
        // Draw border for better separation (at higher zoom levels)
        if (zoomLevel > 2 && w > 4) {
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
          ctx.lineWidth = 1;
          ctx.strokeRect(x, startY, w, rowHeight);
        }
      });

      // Draw row label with theme-aware background
      ctx.fillStyle = isDark ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.9)';
      ctx.fillRect(5, startY + 5, 100, 25);
      ctx.fillStyle = isDark ? '#fff' : arrayLike.color;
      ctx.font = `bold 16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto`;
      ctx.fillText(arrayLike.name, 10, startY + 22);
    });

    if (dtempoTicks.length > 0) {
      const startY = arrayLikes.length * rowHeight;
      const minBpm = 70;
      const maxBpm = 200;
      ctx.fillStyle = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
      ctx.fillRect(0, startY, width, rowHeight);
      ctx.beginPath();
      let started = false;
      dtempoTicks.forEach((tick) => {
        const mid = tick.start + tick.duration / 2;
        if (mid < viewStart || mid > viewEnd) return;
        const x = ((mid - viewStart) / viewDuration) * width;
        const yNorm = (tick.tempo - minBpm) / (maxBpm - minBpm);
        const y = startY + rowHeight - (Math.min(1, Math.max(0, yNorm)) * (rowHeight - 16) + 8);
        if (!started) {
          ctx.moveTo(x, y);
          started = true;
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.strokeStyle = '#E91E63';
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.fillStyle = isDark ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.9)';
      ctx.fillRect(5, startY + 5, 100, 25);
      ctx.fillStyle = isDark ? '#fff' : '#E91E63';
      ctx.font = `bold 16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto`;
      ctx.fillText('Tempo', 10, startY + 22);
    }

  }, [data, zoomLevel, viewStart, viewEnd, viewDuration, isDark]);

  // Calculate position marker position (CSS-based for performance)
  // Using CSS transforms instead of canvas redrawing eliminates lag from:
  // - Clearing canvas every frame (~10-20ms)
  // - Redrawing base image from data URL (~20-50ms)
  // - Drawing marker on canvas (~5-10ms)
  // CSS transitions are GPU-accelerated and much smoother
  const positionMarkerVisible = currentPosition > 0 && currentPosition >= viewStart && currentPosition <= viewEnd;
  const positionMarkerPercent = positionMarkerVisible 
    ? ((currentPosition - viewStart) / viewDuration) * 100 
    : 0;

  if (!data.track?.duration) {
    return null;
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Click to seek (accounts for zoom/pan)
  const handleCanvasClick = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onSeek || !data.track?.duration || isDragging) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const relativePosition = (x / rect.width) * viewDuration;
    const absolutePosition = viewStart + relativePosition;

    onSeek(absolutePosition);
  }, [onSeek, data.track?.duration, viewStart, viewDuration, isDragging]);
  
  // Hover to show info
  const handleCanvasHover = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || !data.track?.duration) return;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const relativePosition = (x / rect.width) * viewDuration;
    const hoverTime = viewStart + relativePosition;
    
    setHoverPosition({ x: event.clientX, y: event.clientY });

    // Find what's at this position
    const info: string[] = [`Time: ${formatTime(hoverTime)}`];
    
    // Check sections
    const section = data.sections?.find(s => hoverTime >= s.start && hoverTime < s.start + s.duration);
    if (section) {
      const sectionIndex = data.sections!.indexOf(section) + 1;
      info.push(`Section ${sectionIndex} (${formatTime(section.duration)} long)`);
    }
    
    // Check bars
    const bar = data.bars?.find(b => hoverTime >= b.start && hoverTime < b.start + b.duration);
    if (bar) {
      const barIndex = data.bars!.indexOf(bar) + 1;
      info.push(`Bar ${barIndex}`);
    }
    
    // Check beats
    const beat = data.beats?.find(b => hoverTime >= b.start && hoverTime < b.start + b.duration);
    if (beat) {
      const beatIndex = data.beats!.indexOf(beat) + 1;
      info.push(`Beat ${beatIndex}`);
    }

    const bpm = tempoAtTime(data.tempo_ticks, hoverTime, 'dtempo');
    const beatBpm = tempoAtTime(data.tempo_ticks, hoverTime, 'beat_ioi');
    if (bpm != null) {
      info.push(`Tempo ${Math.round(bpm)} BPM`);
    }
    if (beatBpm != null && (bpm == null || Math.abs(beatBpm - bpm) >= 4)) {
      info.push(`Beat IOI ${Math.round(beatBpm)} BPM`);
    }

    setHoveredInfo(info.join(' • '));
  }, [data, viewStart, viewDuration]);
  
  // Drag to pan
  const handleMouseDown = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    if (zoomLevel === 1) return; // No panning when fully zoomed out
    setIsDragging(true);
    setDragStartX(event.clientX);
    setDragStartViewStart(viewStart);
  }, [zoomLevel, viewStart]);
  
  const handleMouseMove = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging) {
      handleCanvasHover(event);
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const deltaX = event.clientX - dragStartX;
    const deltaTime = -(deltaX / rect.width) * viewDuration;
    const newViewStart = Math.max(0, Math.min(duration - viewDuration, dragStartViewStart + deltaTime));
    
    setViewStart(newViewStart);
  }, [isDragging, dragStartX, dragStartViewStart, viewDuration, duration, handleCanvasHover]);
  
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
    setHoveredInfo(null);
    setHoverPosition(null);
  }, []);
  
  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => {
      const next = Math.min(prev * 1.5, 20);
      analytics.audioTimelineZoomed('in', next);
      return next;
    });
  }, []);
  
  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => {
      const next = Math.max(prev / 1.5, 1);
      analytics.audioTimelineZoomed('out', next);
      return next;
    });
  }, []);
  
  const handleResetZoom = useCallback(() => {
    setZoomLevel(1);
    setViewStart(0);
    analytics.audioTimelineZoomed('reset', 1);
  }, []);
  
  // Set up native wheel event listener with passive: false to prevent trackpad scrolling
  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container) return;
    
    const handleNativeWheel = (event: WheelEvent) => {
      // Only zoom if Ctrl (Windows/Linux) or Cmd (Mac) is held
      if (!event.ctrlKey && !event.metaKey) {
        return; // Let normal page scroll happen
      }
      
      // Prevent both default browser zoom AND page scroll when zooming timeline
      event.preventDefault();
      event.stopPropagation();
      
      const delta = event.deltaY;
      
      if (delta < 0) {
        handleZoomIn();
      } else {
        handleZoomOut();
      }
    };
    
    // Use passive: false to allow preventDefault() on trackpads
    container.addEventListener('wheel', handleNativeWheel, { passive: false });
    
    return () => {
      container.removeEventListener('wheel', handleNativeWheel);
    };
  }, [handleZoomIn, handleZoomOut]);

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      {/* Header with Zoom Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Timeline Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {zoomLevel === 1 
              ? 'Visual breakdown of beats, bars, sections, and segments'
              : `Zoomed ${zoomLevel.toFixed(1)}x • Showing ${formatTime(viewStart)} - ${formatTime(viewEnd)}`
            }
          </Typography>
        </Box>
        
        {/* Zoom Controls */}
        <ButtonGroup size="small" variant="outlined">
          <IconButton onClick={handleZoomOut} disabled={zoomLevel === 1} size="small">
            <ZoomOut fontSize="small" />
          </IconButton>
          <IconButton onClick={handleResetZoom} disabled={zoomLevel === 1} size="small">
            <ZoomOutMap fontSize="small" />
          </IconButton>
          <IconButton onClick={handleZoomIn} disabled={zoomLevel >= 20} size="small">
            <ZoomIn fontSize="small" />
          </IconButton>
        </ButtonGroup>
      </Box>
      
      {/* Pan Slider (when zoomed) */}
      {zoomLevel > 1 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Pan: {formatTime(viewStart)} → {formatTime(viewEnd)}
          </Typography>
          <Slider
            value={viewStart}
            min={0}
            max={Math.max(0, duration - viewDuration)}
            step={0.1}
            onChange={(_, value) => setViewStart(value as number)}
            valueLabelDisplay="auto"
            valueLabelFormat={formatTime}
            size="small"
          />
        </Box>
      )}
      
      <Box 
        ref={canvasContainerRef}
        sx={{ position: 'relative', width: '100%', bgcolor: 'action.hover', borderRadius: 2, overflow: 'hidden' }}
      >
        <canvas 
          ref={canvasRef} 
          onClick={handleCanvasClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          style={{ 
            display: 'block', 
            width: '100%', 
            cursor: isDragging ? 'grabbing' : (zoomLevel > 1 ? 'grab' : (onSeek ? 'pointer' : 'default')),
          }} 
        />
        
        {/* Playback Position Marker (CSS Overlay for Performance) */}
        {positionMarkerVisible && (
          <Box
            sx={{
              position: 'absolute',
              left: `${positionMarkerPercent}%`,
              top: 0,
              bottom: 0,
              width: '2px',
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              boxShadow: '0 0 8px rgba(0, 0, 0, 0.5)',
              pointerEvents: 'none',
              zIndex: 10,
              transition: 'left 0.1s linear',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: '-3px',
                top: 0,
                bottom: 0,
                width: '8px',
                bgcolor: 'rgba(0, 0, 0, 0.7)',
              }
            }}
          >
            {/* Time Label */}
            <Box
              sx={{
                position: 'absolute',
                top: 5,
                left: '50%',
                transform: 'translateX(-50%)',
                bgcolor: 'rgba(0, 0, 0, 0.9)',
                color: 'white',
                px: 1,
                py: 0.5,
                borderRadius: 0.5,
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              {formatTime(currentPosition)}
            </Box>
          </Box>
        )}
        
        {/* Hover Tooltip */}
        {hoveredInfo && hoverPosition && (
          <Box
            sx={{
              position: 'fixed',
              left: hoverPosition.x + 10,
              top: hoverPosition.y + 10,
              bgcolor: 'rgba(0, 0, 0, 0.9)',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 1,
              fontSize: '0.875rem',
              pointerEvents: 'none',
              zIndex: 9999,
              whiteSpace: 'nowrap',
            }}
          >
            {hoveredInfo}
          </Box>
        )}
        
        {/* Time markers */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1, bgcolor: 'background.paper' }}>
          <Typography variant="caption" color="text.secondary">
            {formatTime(viewStart)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatTime((viewStart + viewEnd) / 2)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatTime(viewEnd)}
          </Typography>
        </Box>
      </Box>

      {/* Stats and Instructions */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        {/* Legend */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {[
            data.sections && { name: 'Sections', count: data.sections.length, color: '#4CAF50' },
            data.bars && { name: 'Bars', count: data.bars.length, color: '#2196F3' },
            data.beats && { name: 'Beats', count: data.beats.length, color: '#FF9800' },
            data.tatums && { name: 'Tatums', count: data.tatums.length, color: '#9C27B0' },
            data.segments && { name: 'Segments', count: data.segments.length, color: '#607D8B' },
            data.tempo_ticks && { name: 'Tempo ticks', count: data.tempo_ticks.length, color: '#E91E63' },
          ].filter(Boolean).map((item) => (
            <Box key={item!.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: item!.color, borderRadius: 0.5 }} />
              <Typography variant="caption" fontWeight="medium">
                {item!.name}:
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item!.count}
              </Typography>
            </Box>
          ))}
        </Box>
        
        {/* Instructions */}
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {onSeek ? '🖱️ Click to seek • ' : ''}
          {zoomLevel > 1 ? '🖐️ Drag to pan • ' : ''}
          🔍 Ctrl+Scroll to zoom • Hover for details
        </Typography>
      </Box>
    </Paper>
  );
}
